import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Paper, Skeleton, Typography } from '@mui/material';

import { useWidgetSummary } from '../../hooks/useWidgetSummary';
import { formatMetric, buildLinkTo } from '../../widgets/summaryUtils';

// 메인화면 비주얼 리뉴얼(2026-10-23) - 상단 "숫자 띠": 오늘 할 일/조치 건수를 세트별로
// 한 줄(좁은 화면에서는 줄바꿈)로 모아 보여준다. 각 칸은 기존 위젯이 이미 쓰는 딥링크를
// 그대로 재사용한다(새 백엔드 API 없음). "결재 대기"는 모든 세트에 공통으로 섞여 있는
// COMMON_APPROVAL_INBOX 위젯 몫인데 그 위젯은 getWidgetSummary 대상이 아니라(기존
// ApprovalNowListContainer가 redux로 따로 관리) 여기서는 그 redux 상태를 구독만 한다
// (dispatch는 ApprovalNowListContainer가 이미 하므로 중복 호출하지 않는다) - useSelector는
// 옵셔널 체이닝으로 방어해서, 이 컴포넌트가 (테스트 등에서) 그 슬라이스가 없는 스토어 위에
// 올라가도 죽지 않게 한다.
// 접근성(재검토 2, 2026-10-23 §6-13): to가 없을 때 '#'로 채우면 "아무 데도 안 가는 링크"가
// 생긴다(스크린리더/키보드 사용자에게는 눌러도 반응 없는 링크로 읽힌다) - 링크가 없으면
// RouterLink가 아니라 그냥 div로 그린다(포커스 가능한 거짓 링크를 만들지 않는다).
const KpiTile = ({ label, value, to, loading }) => (
  <Paper
    {...(to ? { component: RouterLink, to } : { component: 'div' })}
    variant="outlined"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minWidth: 112,
      px: 1.5,
      py: 0.75,
      borderRadius: 1.5,
      borderColor: 'divider',
      textDecoration: 'none',
      color: 'inherit',
      transition: 'border-color .15s, background-color .15s',
      '&:hover': to ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : undefined,
    }}
  >
    <Typography variant="caption" color="text.secondary" noWrap>
      {label}
    </Typography>
    {loading ? (
      <Skeleton variant="text" width={48} height={26} />
    ) : (
      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} noWrap>
        {value}
      </Typography>
    )}
  </Paper>
);

const ApprovalKpiTile = () => {
  const { approvals, loading } = useSelector(({ userModule, loading: loadingMap } = {}) => ({
    approvals: userModule?.approvals?.approvals,
    loading: loadingMap?.['user/LIST_USER_APPROVALS'],
  }));

  return (
    <KpiTile
      label="결재 대기"
      value={`${(approvals || []).length}건`}
      to="/approvalmng"
      loading={loading && !approvals}
    />
  );
};

// COMMON_APPROVAL_SUBMITTED("내 상신 진행")는 COMMON 세트라 모든 세트에 항상 병합되므로
// (HomeLayoutService) 결재 대기와 짝으로 모든 세트의 KPI 띠에 둔다(비주얼 리뉴얼 2단계,
// 2026-10-23 §롤아웃). dispatch는 ApprovalSubmittedListContainer가 이미 하므로(같은 화면에
// COMMON 위젯으로 항상 렌더됨) 여기서는 redux 상태만 구독한다 - 중복 호출 없음. 필터 규칙은
// 그 컨테이너와 동일(완료 제외).
const SubmittedKpiTile = () => {
  const { approvals, loading } = useSelector(({ approvalModule, loading: loadingMap } = {}) => ({
    approvals: approvalModule?.approvals?.approvals,
    loading: loadingMap?.['approval/LIST_APPROVALS'],
  }));
  const inProgressOrRejected = (approvals || []).filter((a) => a.docuStatus !== 'Y');

  return (
    <KpiTile
      label="내 상신"
      value={`${inProgressOrRejected.length}건`}
      to="/approvalmng?searchType=N"
      loading={loading && !approvals}
    />
  );
};

const WidgetKpiTile = ({ widgetCd, fallbackLabel }) => {
  const { loading, forbidden, summary } = useWidgetSummary(widgetCd);

  if (forbidden) return null;
  if (!loading && !summary?.metric) return null;

  const label = summary?.metric?.label || fallbackLabel;
  const to = summary?.link?.path ? buildLinkTo(summary.link) : undefined;

  return (
    <KpiTile
      label={label}
      value={!loading && summary ? formatMetric(summary.metric) : ''}
      to={to}
      loading={loading}
    />
  );
};

// widgets: DashboardContainer가 내려주는, 이미 레이아웃 API로 받은(= 새 백엔드 호출 없는)
// 세트별 "일반 위젯" 목록(EXTERNAL/캘린더 제외). 각 칸은 getWidgetSummary를 한 번씩 더
// 불러 숫자만 압축해서 보여준다(카드와 같은 데이터, 중복 호출 - 이번 프로토타입 단계의
// 알려진 비효율, report에 기록).
const KpiStrip = ({ widgets = [] }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      mb: 1.5,
    }}
  >
    <ApprovalKpiTile />
    <SubmittedKpiTile />
    {widgets.map((w) => (
      <WidgetKpiTile key={w.widgetCd} widgetCd={w.widgetCd} fallbackLabel={w.widgetNm} />
    ))}
  </Box>
);

export default KpiStrip;
